import { useMemo } from 'react';
import { type WorkspaceType, WorkspaceTypeID } from '../../lib/api/workspaces/dto';
import { DEFAULT_STALE_TIME } from '../../lib/queryClient';
import { useGetWorkspaceTypesQuery } from '../api/useWorkspaces';
import { FeatureFlag, useFeatureFlag } from '../useFeatureFlag';
import { usePermissions } from '../usePermissions';

const getSortedAvailableWorkspaceTypes = (
  hasAccessToWorkspace: (typeId: number) => boolean,
  workspaceTypes?: WorkspaceType[],
) =>
  (workspaceTypes || [])
    .filter((workspace) => hasAccessToWorkspace(workspace.type_id))
    .sort((a, b) => a.type_ordinal_position - b.type_ordinal_position);

export const useWorkspaceTypes = () => {
  const { hasAccessToWorkspace, isPermissionDataLoading, email } = usePermissions();

  const { enabled: isHcpExpansionEnabled } = useFeatureFlag(FeatureFlag.HcpExpansionWorkspace);
  const { enabled: isDtcWorkspaceEnabled } = useFeatureFlag(FeatureFlag.DtcWorkspace);

  const {
    data: workspaceTypesData,
    isLoading: areWorkspaceTypesLoading,
    isFetching: areWorkspaceTypesFetching,
    error: workspaceTypesError,
    isError: isWorkspaceTypesError,
    isSuccess: areWorkspaceTypesSuccess,
  } = useGetWorkspaceTypesQuery({ staleTime: 2 * DEFAULT_STALE_TIME });

  const workspaceTypes = useMemo(() => {
    const data = workspaceTypesData?.items ?? [];

    const featureFlagsEnabledByTypeId: Partial<Record<WorkspaceTypeID, boolean>> = {
      [WorkspaceTypeID.HCPExpansion]: isHcpExpansionEnabled ?? true,
      [WorkspaceTypeID.DTC]: isDtcWorkspaceEnabled ?? true,
    };

    const filteredWorkspaceTypes = data.filter((t) => {
      const enabled = featureFlagsEnabledByTypeId[t.type_id as WorkspaceTypeID];
      return enabled === undefined ? true : enabled;
    });

    return getSortedAvailableWorkspaceTypes(hasAccessToWorkspace, filteredWorkspaceTypes);
  }, [hasAccessToWorkspace, workspaceTypesData?.items, isHcpExpansionEnabled, isDtcWorkspaceEnabled]);

  const areWorkspaceTypesLoaded = useMemo(
    () => !areWorkspaceTypesLoading && !isPermissionDataLoading && email,
    [areWorkspaceTypesLoading, isPermissionDataLoading, email],
  );

  return {
    workspaceTypesData,
    workspaceTypes,
    areWorkspaceTypesLoading,
    areWorkspaceTypesLoaded,
    areWorkspaceTypesFetching,
    workspaceTypesError,
    isWorkspaceTypesError,
    areWorkspaceTypesSuccess,
  };
};
